import BoltIcon from '@mui/icons-material/Bolt';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import InfoIcon from '@mui/icons-material/Info';
// Icons
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Typography } from '@mui/material';
import React, { memo, useMemo } from 'react';
import { useDeviceStore } from '../../stores/deviceStore';
import { SensorBox } from './SensorBox';

/**
 * @typedef {Object} ChamberSensors
 * @property {import('../../types/DeviceDescriptor').O2TemperatureSensor[]} o2temperatureSensor
 * @property {import('../../types/DeviceDescriptor').O2StatusSensor[]} o2statusSensor
 * @property {import('../../types/DeviceDescriptor').O2Sensor[]} o2Sensor
 * @property {import('../../types/DeviceDescriptor').ChamberSensor[]} chamberTemperatureSensor
 * @property {import('../../types/DeviceDescriptor').HeatsinkSensor[]} chamberHeatsinkTemperatureSensor
 * @property {import('../../types/DeviceDescriptor').OvertemperatureSensor[]} overtemperatureSensor
 * @property {import('../../types/DeviceDescriptor').HeatingPowerSensor[]} heatingPowerSensor
 */

// Helper to extract chamber number from selectedTab (e.g., "O2C123456-Sensor-01" -> 1)
function extractChamberNumber(selectedTab) {
  const match = selectedTab.split('-').pop();
  if (match && /^\d+$/.test(match)) {
    return parseInt(match, 10);
  }
  return null;
}

// Helper to find sensor for a specific chamber from sensors with chambers array
function findSensorForChamber(sensors, chamberNumber) {
  return sensors.find((sensor) => sensor.chambers.includes(chamberNumber)) ?? null;
}

/** @param {{ chamberSensors: ChamberSensors }} props */
export const ShowChamberSensors = memo(({ chamberSensors }) => {
  const selectedTab = useDeviceStore((state) => state.selectedTab);

  // Extract chamber number from selected tab
  const chamberNumber = useMemo(() => extractChamberNumber(selectedTab), [selectedTab]);

  // Get sensors for the selected chamber
  const sensors = useMemo(() => {
    if (chamberNumber === null) return null;

    // O2 sensors: one per chamber, index by (chamberNumber - 1)
    const chamberIndex = chamberNumber - 1;
    const o2Temp = chamberSensors.o2temperatureSensor[chamberIndex] ?? null;
    const o2Status = chamberSensors.o2statusSensor[chamberIndex] ?? null;
    const o2 = chamberSensors.o2Sensor[chamberIndex] ?? null;

    // Temperature sensors: find by chambers array
    const chamberTemp = findSensorForChamber(
      chamberSensors.chamberTemperatureSensor,
      chamberNumber,
    );
    const heatsink = findSensorForChamber(
      chamberSensors.chamberHeatsinkTemperatureSensor,
      chamberNumber,
    );
    const overtemp = findSensorForChamber(chamberSensors.overtemperatureSensor, chamberNumber);
    const heatingPower = findSensorForChamber(chamberSensors.heatingPowerSensor, chamberNumber);

    return { o2Temp, o2Status, o2, chamberTemp, heatsink, overtemp, heatingPower };
  }, [chamberNumber, chamberSensors]);

  // Get reference IDs for selectors
  const o2TempRefId = sensors?.o2Temp?.referenceId ?? '';
  const o2StatusRefId = sensors?.o2Status?.referenceId ?? '';
  const o2RefId = sensors?.o2?.referenceId ?? '';
  const chamberTempRefId = sensors?.chamberTemp?.referenceId ?? '';
  const heatsinkRefId = sensors?.heatsink?.referenceId ?? '';
  const overtempRefId = sensors?.overtemp?.referenceId ?? '';
  const heatingPowerRefId = sensors?.heatingPower?.referenceId ?? '';

  // Use selectors to get latest values
  const o2TempValue = useDeviceStore((state) => {
    if (!o2TempRefId) return null;
    const values = state.chamberData[o2TempRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  const o2StatusValue = useDeviceStore((state) => {
    if (!o2StatusRefId) return null;
    const values = state.chamberData[o2StatusRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  const o2Value = useDeviceStore((state) => {
    if (!o2RefId) return null;
    const values = state.chamberData[o2RefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  const chamberTempValue = useDeviceStore((state) => {
    if (!chamberTempRefId) return null;
    const values = state.chamberData[chamberTempRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  const heatsinkValue = useDeviceStore((state) => {
    if (!heatsinkRefId) return null;
    const values = state.chamberData[heatsinkRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  const overtempValue = useDeviceStore((state) => {
    if (!overtempRefId) return null;
    const values = state.chamberData[overtempRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  const heatingPowerValue = useDeviceStore((state) => {
    if (!heatingPowerRefId) return null;
    const values = state.chamberData[heatingPowerRefId]?.values;
    return values && values.length > 0 ? values[values.length - 1] : null;
  });

  // Don't render if no chamber selected
  if (chamberNumber === null || !sensors) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Typography>Chamber Sensors:</Typography>

      {/* O2 Temperature */}
      {sensors.o2Temp && (
        <SensorBox
          icon={<ThermostatIcon fontSize="small" />}
          value={
            o2TempValue !== null
              ? `${Number(o2TempValue).toFixed(2)} ${sensors.o2Temp.unit}`
              : 'N/A'
          }
          tooltipText={`${sensors?.o2Temp?.description ?? ''}`}
        />
      )}

      {/* O2 Status */}
      {sensors.o2Status && (
        <SensorBox
          icon={<InfoIcon fontSize="small" />}
          value={o2StatusValue !== null ? String(o2StatusValue) : 'N/A'}
          tooltipText={`${sensors?.o2Status?.description ?? ''}`}
        />
      )}

      {/* Chamber Temperature */}
      {sensors.chamberTemp && (
        <SensorBox
          icon={<DeviceThermostatIcon fontSize="small" />}
          value={
            chamberTempValue !== null
              ? `${Number(chamberTempValue).toFixed(2)} ${sensors.chamberTemp.unit}`
              : 'N/A'
          }
          tooltipText={`${sensors?.chamberTemp?.description ?? ''}`}
        />
      )}

      {/* Heatsink Temperature */}
      {sensors.heatsink && (
        <SensorBox
          icon={<DeviceThermostatIcon fontSize="small" />}
          value={
            heatsinkValue !== null
              ? `${Number(heatsinkValue).toFixed(2)} ${sensors.heatsink.unit}`
              : 'N/A'
          }
          tooltipText={`${sensors?.heatsink?.description ?? ''}`}
        />
      )}

      {/* Overtemperature */}
      {sensors.overtemp && (
        <SensorBox
          icon={<WarningIcon fontSize="small" />}
          value={
            overtempValue !== null
              ? `${Number(overtempValue).toFixed(2)} ${sensors.overtemp.unit}`
              : 'N/A'
          }
          tooltipText={`${sensors?.overtemp?.description ?? ''}`}
        />
      )}

      {/* Heating Power */}
      {sensors.heatingPower && (
        <SensorBox
          icon={<BoltIcon fontSize="small" />}
          value={
            heatingPowerValue !== null
              ? `${Number(heatingPowerValue).toFixed(2)} ${sensors.heatingPower.unit}`
              : 'N/A'
          }
          tooltipText={`${sensors?.heatingPower?.description ?? ''}`}
        />
      )}
    </Box>
  );
});

ShowChamberSensors.displayName = 'ShowChamberSensors';
